import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as PackageIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import PackageService from '../../services/PackageService';
import PatientService from '../../services/PatientService';

const PackageList = ({ onCreatePackage, onEditPackage, onManagePatientPackages }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [patientSelectDialogOpen, setPatientSelectDialogOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await PackageService.getAllPackages();
      setPackages(data);
      setError('');
    } catch (err) {
      setError('Failed to fetch packages');
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (pkg) => {
    setPackageToDelete(pkg);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!packageToDelete) return;

    try {
      await PackageService.deletePackage(packageToDelete.packageId);
      setPackages(packages.filter(p => p.packageId !== packageToDelete.packageId));
      setDeleteDialogOpen(false);
      setPackageToDelete(null);
    } catch (err) {
      setError('Failed to delete package');
      console.error('Error deleting package:', err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPackageToDelete(null);
  };

  const handleManagePatientPackages = async () => {
    setPatientSelectDialogOpen(true);
    setLoadingPatients(true);
    setError(''); // Clear any previous errors
    try {
      const response = await PatientService.searchPatients('');
      console.log('Patients response received:', response); // Debug log
      
      // Extract the actual patient data from the Axios response
      const patientsData = response.data || [];
      console.log('Extracted patients data:', patientsData);
      console.log('Number of patients:', patientsData.length);
      
      setPatients(patientsData);
    } catch (error) {
      console.error('Error loading patients:', error);
      setError('Failed to load patients: ' + (error.message || error.toString()));
    } finally {
      setLoadingPatients(false);
    }
  };

  const handlePatientSelect = () => {
    if (selectedPatientId && onManagePatientPackages) {
      onManagePatientPackages(selectedPatientId);
      setPatientSelectDialogOpen(false);
      setSelectedPatientId('');
    }
  };

  const handlePatientSelectCancel = () => {
    setPatientSelectDialogOpen(false);
    setSelectedPatientId('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Package Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={handleManagePatientPackages}
          >
            Manage Patient Packages
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreatePackage}
          >
            Create Package
          </Button>
        </Box>
      </Box>

      {error && (
        <Card sx={{ mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <CardContent>
            <Typography>{error}</Typography>
          </CardContent>
        </Card>
      )}

      {packages.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PackageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No packages found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Create your first package to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreatePackage}
            >
              Create Package
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Sessions</TableCell>
                <TableCell align="center">Free Sessions</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.packageId} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {pkg.packageName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {pkg.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(pkg.price)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={pkg.includedSessions} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={pkg.freeSessions || 0} 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={pkg.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={pkg.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit Package">
                      <IconButton
                        size="small"
                        onClick={() => onEditPackage(pkg)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Package">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(pkg)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Package
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the package "{packageToDelete?.packageName}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Selection Dialog */}
      <Dialog
        open={patientSelectDialogOpen}
        onClose={handlePatientSelectCancel}
        aria-labelledby="patient-select-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="patient-select-dialog-title">
          Select Patient for Package Management
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please select a patient to manage their packages.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {loadingPatients ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="patient-select-label">Select Patient</InputLabel>
              <Select
                labelId="patient-select-label"
                value={selectedPatientId}
                label="Select Patient"
                onChange={(e) => setSelectedPatientId(e.target.value)}
              >
                {patients && patients.length > 0 ? (
                  patients.map((patient) => (
                    <MenuItem key={patient.patientId} value={patient.patientId}>
                      {patient.firstName} {patient.lastName} - {patient.email}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No patients found</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePatientSelectCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handlePatientSelect} 
            color="primary" 
            variant="contained"
            disabled={!selectedPatientId}
          >
            Manage Packages
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageList;
